import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect, useMemo, useState } from 'react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import Loader from '../../../../components/Looder';
import AddAdminUserModal from './AddAdminUserModal';
import EditAdminUserModal from './EditAdminUserModal';
import ViewAdminUserModal from './ViewAdminUserModal';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import { getUsersReq, setUserStatusReq, userErrorMessage } from '../../../../axios/request/SocietyController/CtrlUserReqs/CtrlUserReqs';
import webTokenStorer from '../../../../utils/webStorer';
import { notify } from '../../../../utils/notification';
import { getSocietyDropdownReq } from '../../../../axios/request/SocietyController/CtrlEmployeeReqs/CtrlEmployeeReqs';
import { USER_TYPE_OPTIONS, UserTypeChips, userTypeLabel } from './userTypes';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
];

const formatAdminName = (user) =>
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || '-';

const AdminUsersListLayer = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [societyFilter, setSocietyFilter] = useState('all');
    const [societyOptions, setSocietyOptions] = useState([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    // Rows with a status change in flight — their toggle is disabled until it settles.
    const [statusPendingIds, setStatusPendingIds] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    // Bumped after a successful edit to re-run the fetch below.
    const [refreshTick, setRefreshTick] = useState(0);

    // Debounce search input (300ms) before hitting the API.
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Communities for the scope filter.
    useEffect(() => {
        getSocietyDropdownReq({ data: {} }).then((res) => {
            const rows = Array.isArray(res?.data) ? res.data : [];
            setSocietyOptions(rows
                .map((s) => ({ value: s.value ?? s.society_id, label: s.label ?? s.name }))
                .filter((o) => o.value != null && o.label));
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        const fetchUsers = async () => {
            setIsLoading(true);
            setFetchError(null);
            try {
                const params = {
                    offset: (currentPage - 1) * pageSize,
                    limit: pageSize,
                };
                if (debouncedSearch) params.search = debouncedSearch;
                if (statusFilter === 'Active') params.isActive = 1;
                else if (statusFilter === 'Inactive') params.isActive = 0;
                if (typeFilter !== 'all') params.userType = typeFilter;
                // Staff live on the Employees screen - keep them out of the
                // default listing. Picking the Employee filter still shows them.
                else params.excludeUserTypes = 'EMPLOYEE';
                // Scopes both the rows and each user's user_type to this community.
                if (societyFilter !== 'all') params.societyId = societyFilter;

                const res = await getUsersReq(params);
                if (cancelled) return;
                setUsers(Array.isArray(res?.data) ? res.data : []);
                setTotal(res?.meta?.pagination?.total ?? 0);
            } catch (err) {
                if (cancelled) return;
                setFetchError(userErrorMessage(err) || 'Failed to load users');
                setUsers([]);
                setTotal(0);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchUsers();
        return () => { cancelled = true; };
    }, [currentPage, pageSize, debouncedSearch, statusFilter, typeFilter, societyFilter, refreshTick]);

    const handlePageChange = (next) => {
        if (next >= 1 && next <= Math.max(1, Math.ceil(total / pageSize)) && next !== currentPage) {
            setCurrentPage(next);
        }
    };
    const handleLimitChange = (e) => {
        setPageSize(Number(e.target.value) || 10);
        setCurrentPage(1);
    };
    const handleClear = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setTypeFilter('all');
        setSocietyFilter('all');
        setCurrentPage(1);
    };

    // Map API user rows onto the row shape the table renders.
    const paginatedAdmins = useMemo(
        () =>
            users.map((u) => ({
                id: u.id ?? u.user_id,
                username: u.email || u.phone || '-',
                adminName: formatAdminName(u),
                userType: u.user_type || u.user_types?.[0] || (u.is_super_admin ? 'SUPER_ADMIN' : 'OTHER'),
                userTypes: Array.isArray(u.user_types) ? u.user_types : [],
                type: userTypeLabel(u.user_type || u.user_types?.[0] || (u.is_super_admin ? 'SUPER_ADMIN' : 'OTHER')),
                city: u.city || '-',
                status: u.is_active ? 'Active' : 'Inactive',
                email: u.email || '',
                raw: u,
            })),
        [users]
    );

    const startIndex = (currentPage - 1) * pageSize;

    // Handle modal functions
    const handleAddClick = () => setAddModalOpen(true);
    const handleCloseAddModal = () => setAddModalOpen(false);

    const handleEditClick = (admin) => {
        setSelectedAdmin(admin);
        setEditModalOpen(true);
    };
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedAdmin(null);
    };

    const handleViewClick = (admin) => {
        setSelectedAdmin(admin);
        setViewModalOpen(true);
    };
    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedAdmin(null);
    };

    // The API rejects changing your own status, so that row gets no toggle.
    const selfId = webTokenStorer.getSessionUserId();

    // Update a row in place from the API response (the user stays in the list).
    const patchRow = (id, patch) => {
        setUsers((list) => list.map((u) => (
            String(u.id ?? u.user_id) === String(id) ? { ...u, ...patch } : u
        )));
    };

    // PATCH /api/users/:id/status — on = activate, off = deactivate (logs the
    // user out everywhere; society memberships are kept).
    const handleStatusToggle = async (admin, on) => {
        const id = admin.id;
        const isActive = on ? 1 : 0;
        setStatusPendingIds((ids) => [...ids, id]);
        try {
            const res = await setUserStatusReq(id, isActive);
            if (res?.success === false) throw new Error(res?.message);
            const { revoked_sessions: revoked = 0, ...user } = res?.data || {};
            patchRow(id, { ...user, is_active: isActive });
            notify('success', on
                ? 'User activated'
                : `User deactivated${revoked ? ` · ${revoked} session${revoked === 1 ? '' : 's'} logged out` : ''}`);
        } catch (err) {
            if (err?.response?.status === 409) {
                // Already in that state — the list was just stale.
                patchRow(id, { is_active: isActive });
                notify('info', userErrorMessage(err));
            } else {
                notify('error', userErrorMessage(err));
            }
        } finally {
            setStatusPendingIds((ids) => ids.filter((x) => x !== id));
        }
    };

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            {/* Filters Bar */}
            <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-relative">
                        <Icon icon="ion:search-outline" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                        <input
                            type="text"
                            placeholder="Search name, email or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="tw-h-8 tw-pl-8 tw-pr-3 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 focus:tw-ring-1 focus:tw-ring-rose-500 tw-w-56"
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={typeFilter || 'all'}
                            onChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}
                            options={USER_TYPE_OPTIONS}
                            placeholder="Type"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-48">
                        <SearchableDropdown
                            value={societyFilter || 'all'}
                            onChange={(v) => { setSocietyFilter(v || 'all'); setCurrentPage(1); }}
                            options={[{ value: 'all', label: 'All Communities' }, ...societyOptions]}
                            placeholder="Community"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={statusFilter || 'all'}
                            onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
                            options={STATUS_OPTIONS}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>
                    <button
                        onClick={handleClear}
                        className="tw-h-8 tw-inline-flex tw-items-center tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        Clear
                    </button>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {/* Re-runs the list fetch with the current filters and page */}
                    <button
                        onClick={() => setRefreshTick((t) => t + 1)}
                        disabled={isLoading}
                        className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-60"
                        title="Reload users"
                    >
                        <Icon icon="mdi:refresh" className={`tw-text-sm ${isLoading ? 'tw-animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                    >
                        <Icon icon="mdi:plus" className="tw-text-sm" />
                        Add Admin
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                            <th style={{ minWidth: 50 }} className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">#</th>
                            <th style={{ minWidth: 240 }} className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">User</th>
                            <th style={{ minWidth: 170 }} className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Type</th>
                            <th style={{ minWidth: 160 }} className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Contact</th>
                            <th style={{ minWidth: 110 }} className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Status</th>
                            <th style={{ minWidth: 70 }} className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Action</th>
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y tw-divide-gray-50">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="tw-text-center tw-py-12">
                                    <Loader />
                                </td>
                            </tr>
                        ) : fetchError ? (
                            <tr>
                                <td colSpan={6} className="tw-text-center tw-py-12">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <Icon icon="mdi:alert-circle-outline" className="tw-text-3xl tw-text-red-300" />
                                        <p className="tw-text-xs tw-text-red-500">{fetchError}</p>
                                    </div>
                                </td>
                            </tr>
                        ) : paginatedAdmins.length > 0 ? (
                            paginatedAdmins.map((admin, index) => (
                                <tr key={admin.id} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-400">{startIndex + index + 1}</td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-rose-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Icon icon="iconamoon:profile-circle-fill" className="tw-text-rose-500 tw-text-base" />
                                            </div>
                                            <div className="tw-min-w-0 tw-font-medium tw-text-gray-800 tw-truncate">{admin.adminName}</div>
                                        </div>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <UserTypeChips type={admin.userType} types={admin.userTypes} />
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                                        <div className="tw-truncate">{admin.email || '—'}</div>
                                        <div className="tw-text-[10px] tw-text-gray-400">{admin.raw?.phone || ''}</div>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border ${
                                            admin.status === 'Active'
                                                ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100'
                                                : 'tw-bg-gray-50 tw-text-gray-500 tw-border-gray-100'
                                        }`}>
                                            <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${admin.status === 'Active' ? 'tw-bg-emerald-500' : 'tw-bg-gray-400'}`}></span>
                                            {admin.status}
                                        </span>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <div className="tw-flex tw-items-center tw-gap-0.5">
                                            {/* Active/Inactive switch — your own row keeps an empty slot so columns line up */}
                                            {String(admin.id) === String(selfId) ? (
                                                <span className="tw-inline-block tw-w-7 tw-mr-1" title="You can't change your own status" />
                                            ) : (
                                                <span
                                                    className="tw-inline-flex tw-items-center tw-mr-1"
                                                    title={statusPendingIds.includes(admin.id)
                                                        ? 'Updating…'
                                                        : admin.status === 'Active' ? 'Deactivate' : 'Activate'}
                                                >
                                                    <ToggleSwitch
                                                        size="sm"
                                                        id={`admin-status-${admin.id}`}
                                                        checked={admin.status === 'Active'}
                                                        disabled={statusPendingIds.includes(admin.id)}
                                                        onChange={(on) => handleStatusToggle(admin, on)}
                                                    />
                                                </span>
                                            )}
                                            <button
                                                onClick={() => handleViewClick(admin)}
                                                className="tw-w-6 tw-h-6 tw-inline-flex tw-items-center tw-justify-center tw-rounded tw-text-gray-500 hover:tw-bg-slate-100 hover:tw-text-slate-700 tw-transition-colors"
                                                title="View"
                                            >
                                                <Icon icon="iconamoon:eye-light" className="tw-text-[13px]" />
                                            </button>
                                            <button
                                                onClick={() => handleEditClick(admin)}
                                                className="tw-w-6 tw-h-6 tw-inline-flex tw-items-center tw-justify-center tw-rounded tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-colors"
                                                title="Edit"
                                            >
                                                <Icon icon="lucide:edit" className="tw-text-[13px]" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="tw-text-center tw-py-12">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <Icon icon="mdi:database-off-outline" className="tw-text-3xl tw-text-gray-300" />
                                        <p className="tw-text-xs tw-text-gray-400">No users found</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {paginatedAdmins.length > 0 && (
                <div className="tw-border-t tw-border-gray-100 tw-px-3 tw-py-2">
                    <PaginationLimit
                        currentPage={currentPage}
                        totalRecords={total}
                        limit={pageSize}
                        handleLimitChange={handleLimitChange}
                        handlePageChange={handlePageChange}
                    />
                </div>
            )}

            {/* Modals */}
            <AddAdminUserModal open={addModalOpen} onClose={handleCloseAddModal} />
            <EditAdminUserModal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                admin={selectedAdmin}
                onSaved={() => setRefreshTick((t) => t + 1)}
            />
            <ViewAdminUserModal open={viewModalOpen} onClose={handleCloseViewModal} admin={selectedAdmin} />
        </div>
    );
};

export default AdminUsersListLayer;
